import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'

/**
 * FF-AUDIT-20260914-210459-028: the header popovers (Sync centre, Facility
 * scope, Notifications, Account menu) each rolled their own outside-click
 * close and nothing else — Escape did nothing, focus never entered the
 * panel, and two or three could be open at once, stacked over each other.
 *
 * This is the one behaviour they share (WAI-ARIA disclosure/menu pattern):
 * - Escape closes and returns focus to the trigger;
 * - opening moves focus to the first focusable control in the panel;
 * - opening one popover closes every other one registered here;
 * - a pointer-down outside the container (and outside the panel, which may
 *   be portalled elsewhere) closes it.
 *
 * The caller renders whatever it likes inside `containerRef`; the first
 * `button` in the container is treated as the trigger for focus return.
 */
type Closer = () => void
const openPopovers = new Map<symbol, Closer>()

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface Popover {
  open: boolean
  setOpen: (next: boolean | ((current: boolean) => boolean)) => void
  /** Wrap the trigger AND the panel in this element. */
  containerRef: RefObject<HTMLDivElement>
  /** The panel element; focus moves into it on open. */
  panelRef: RefObject<HTMLDivElement>
}

export function usePopover(): Popover {
  const [open, setOpenState] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const idRef = useRef(Symbol('floorfile-popover'))

  const setOpen = useCallback((next: boolean | ((current: boolean) => boolean)) => {
    setOpenState((current) => (typeof next === 'function' ? next(current) : next))
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const id = idRef.current
    // Single-open: whoever opens last wins.
    for (const [otherId, close] of openPopovers) if (otherId !== id) close()
    openPopovers.set(id, () => setOpenState(false))

    const trigger = containerRef.current?.querySelector<HTMLElement>('button')
    const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? panelRef.current
    if (first && first !== document.activeElement) first.focus()
    // FF-QA-130: a portalled panel is positioned in a layout effect; if the
    // first control could not take focus in this tick (not yet laid out), try
    // once more after paint rather than leaving focus on the trigger.
    const retry = requestAnimationFrame(() => {
      const panel = panelRef.current
      if (!panel || panel.contains(document.activeElement)) return
      const target = panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel
      target.focus()
    })

    const onKeyDown = (event: KeyboardEvent) => {
      // FF-QA-130: Tab past the panel's last control (or Shift+Tab before its
      // first) closes it and continues from the trigger — a panel portalled to
      // the end of <body> otherwise drops keyboard focus off the page.
      if (event.key === 'Tab' && panelRef.current?.contains(document.activeElement)) {
        const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement)
        const firstEl = focusables[0]
        const lastEl = focusables[focusables.length - 1]
        if (!event.shiftKey && document.activeElement === lastEl) {
          setOpenState(false)
          trigger?.focus()
          return // the default Tab now moves on from the trigger
        }
        if (event.shiftKey && document.activeElement === firstEl) {
          event.preventDefault()
          setOpenState(false)
          trigger?.focus()
          return
        }
      }
      if (event.key !== 'Escape') return
      // A control inside the panel that owns an open popup (combobox) takes
      // Escape first — the same yield the Dialog primitive makes.
      const target = event.target as HTMLElement | null
      if (target?.closest('[role="combobox"][aria-expanded="true"]')) return
      event.preventDefault()
      event.stopPropagation()
      setOpenState(false)
      trigger?.focus()
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      // F34: a panel portalled to <body> (RowMenu, the column chooser) is no
      // longer inside the container, but a press on it is not "outside".
      if (panelRef.current?.contains(target)) return
      if (containerRef.current && !containerRef.current.contains(target)) setOpenState(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      cancelAnimationFrame(retry)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
      if (openPopovers.get(id)) openPopovers.delete(id)
    }
  }, [open])

  return { open, setOpen, containerRef, panelRef }
}
